import {
  BasketExtraServicesViewModel,
  BasketFlightViewModel,
  BasketHotelViewModel,
  BasketTourViewModel,
  BasketTransferViewModel,
  HotelPromotionViewModel,
  TourPromotionViewModel,
  PromotionType,
} from '@api/v3';
import {
  BasketItemServiceParentItemType,
  ExtraServiceType,
  GetSearchModal,
  TransferServiceType,
} from '@components/views/types';

import HotelSearchForm from './components/modals/HotelSearchForm';
import TourSearchForm from './components/modals/TourSearchForm';
import ShipSearchForm from './components/modals/ShipSearchForm';
import FlightSearchForm from './components/modals/FlightSearchForm';
import { getServiceFee } from '../flight/utils';
import TransferServiceForm from './components/modals/transfer-service/TransferServiceForm';
import HotelExtraServiceForm, {
  CANCELLATION_GUARANTEE_TYPE_ID,
} from './hotel/HotelExtraServiceForm';
import TourExtraServiceForm from './tour/TourExtraServiceForm';
import ServiceParentItemInfo from './components/ServiceParentItemInfo';
import CyprusSearchForm from './components/modals/CyprusSearchForm';

export const getSearchModal = ({
  type,
  onCancel,
  onSuccess,
  initialValues,
  setShowLoader,
}: GetSearchModal) => {
  switch (type) {
    case 'hotel':
      return (
        <HotelSearchForm
          key='hotel'
          initialValues={initialValues}
          onCancel={onCancel}
          setShowLoader={setShowLoader}
          isHoliday={false}
        />
      );
    case 'tour':
      return (
        <TourSearchForm
          key='tour'
          setShowLoader={setShowLoader}
          onCancel={onCancel}
        />
      );

    case 'ship':
      return (
        <ShipSearchForm
          setShowLoader={setShowLoader}
          key='ship'
          onCancel={onCancel}
        />
      );
    case 'cyprus':
      return (
        <CyprusSearchForm
          setShowLoader={setShowLoader}
          key='cyprus'
          onCancel={onCancel}
          isHoliday={false}
        />
      );
    case 'flight':
      return (
        <FlightSearchForm
          key='flight'
          initialValues={initialValues}
          onCancel={onCancel}
          setShowLoader={setShowLoader}
          isHoliday={false}
        />
      );
    case 'transfer':
      return (
        <TransferServiceForm
          key='transfer'
          onCancel={onCancel}
          initialValues={initialValues}
          onSuccess={onSuccess}
        />
      );
    case 'hotel-extra-service':
      return (
        <HotelExtraServiceForm
          key='hotel-extra-service'
          onCancel={onCancel}
          initialValues={initialValues}
          onSuccess={onSuccess}
        />
      );
    case 'tour-extra-service':
      return (
        <TourExtraServiceForm
          key='tour-extra-service'
          onCancel={onCancel}
          initialValues={initialValues}
          onSuccess={onSuccess}
        />
      );

    default:
      return null;
  }
};

export const getBasketFlightsTotalServiceFee = (
  flights: BasketFlightViewModel[]
) => {
  if (flights && flights.length) {
    let totalServiceFee = 0;
    flights.forEach((flight) => {
      const { adultCount, childCount } = flight.guests;
      totalServiceFee += getServiceFee({
        outboundFlight: flight.plan.flights[0],
        inboundFlight: flight.plan.flights[1],
        guestCount: adultCount + childCount,
      });
    });
    return totalServiceFee;
  }
  return 0;
};

export const getExtraServicesForBasketItem = (
  itemId: string,
  extraServices: BasketExtraServicesViewModel[],
  isCancellationGuarantee = false
): BasketExtraServicesViewModel[] => {
  return extraServices?.filter(
    (extraService) =>
      (isCancellationGuarantee
        ? extraService.typeId === CANCELLATION_GUARANTEE_TYPE_ID
        : extraService.typeId !== CANCELLATION_GUARANTEE_TYPE_ID) &&
      extraService.parentItemId === itemId
  );
};

export const getTransferServiceWithParentBasketItemInfo = (
  transfers: BasketTransferViewModel[],
  parentItems: BasketItemServiceParentItemType[]
): TransferServiceType[] | undefined => {
  return transfers?.map((transfer) => {
    const foundParentItem = parentItems.find(
      ({ itemId }) => transfer.parentItemId === itemId
    );
    return {
      ...transfer,
      parentItemInfo: foundParentItem ? (
        <ServiceParentItemInfo name={foundParentItem.name} />
      ) : (
        ''
      ),
    };
  });
};

export const getSplitGuaranteeAndExtraServices = (
  extraServices: BasketExtraServicesViewModel[],
  parentItems: BasketItemServiceParentItemType[]
): {
  extraServices: ExtraServiceType[];
  guaranties: ExtraServiceType[];
} => {
  const mapParent = (
    extraService: BasketExtraServicesViewModel
  ): ExtraServiceType => {
    const parentItem = parentItems.find(
      ({ itemId }) => itemId === extraService.parentItemId
    );
    return {
      ...extraService,
      parentItemInfo: parentItem ? (
        <ServiceParentItemInfo
          name={parentItem.name}
          departureDate={parentItem.departureDate}
          arrivalDate={parentItem.arrivalDate}
        />
      ) : (
        ''
      ),
    };
  };

  return {
    extraServices: extraServices
      ?.filter(
        (extraService) => extraService.typeId !== CANCELLATION_GUARANTEE_TYPE_ID
      )
      ?.map(mapParent),
    guaranties: extraServices
      ?.filter(
        (extraService) => extraService.typeId === CANCELLATION_GUARANTEE_TYPE_ID
      )
      ?.map(mapParent),
  };
};

export const formatHotelsAsParentItems = (hotels: BasketHotelViewModel[]) =>
  hotels?.map((hotel) => ({
    itemId: hotel.itemId,
    name: hotel.name,
    arrivalDate: hotel.in,
    departureDate: hotel.out,
  }));

export const formatToursAsParentItems = (hotels: BasketTourViewModel[]) =>
  hotels?.map((tour) => ({
    itemId: tour.itemId,
    name: tour.name,
    arrivalDate: tour.arrivalDate,
    departureDate: tour.departureDate,
  }));

export const getSeturLoyaltySelectedPromotion = (
  item: BasketHotelViewModel | BasketTourViewModel | BasketFlightViewModel
) => {
  if (item?.variations && item?.variations?.length === 0) {
    return undefined;
  }
  const selectedVariation = item.variations.find(
    (variation) => variation.selected
  );
  return selectedVariation?.promotions.find(
    (promotion) =>
      promotion?.loyaltyType && promotion?.loyaltyType === 'SeturPointEarn'
  );
};

export const haveLoyaltyPromotion = (
  promotions: HotelPromotionViewModel[] | TourPromotionViewModel[]
) => {
  const index = promotions.findIndex((promotion) => {
    return promotion?.promotionType === PromotionType.SETUR_POINT_EARN;
  });
  return index >= 0;
};
